/**
 * localStorage, holding the response id and the first name for the greeting.
 * Nothing else, and nothing load-bearing. Spec FR5.
 *
 * localStorage rather than sessionStorage because the acceptance criterion in
 * spec section 13 requires resuming after the tab is closed, which
 * sessionStorage does not survive.
 *
 * Everything here is best-effort. Private browsing, a full quota, or a locked
 * down device can make localStorage throw on read or write, and a respondent
 * must still be able to complete the form when that happens: they simply do not
 * get resume.
 */

const RESPONSE_ID_KEY = 'dna17q.response_id';
const FIRST_NAME_KEY = 'dna17q.first_name';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // No resume for this respondent. Not worth interrupting them over.
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing useful to do.
  }
}

export function readSession(): { responseId: string | null; firstName: string | null } {
  return { responseId: read(RESPONSE_ID_KEY), firstName: read(FIRST_NAME_KEY) };
}

export function saveSession(responseId: string, firstName: string): void {
  write(RESPONSE_ID_KEY, responseId);
  write(FIRST_NAME_KEY, firstName);
}

/**
 * Spec FR5 condition 3. Called the moment a response completes, and whenever a
 * stored id turns out to be complete or unknown. A shared laptop at a live
 * session must never hand the next person someone else's form.
 */
export function clearSession(): void {
  remove(RESPONSE_ID_KEY);
  remove(FIRST_NAME_KEY);
}
